import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import pageProperties from '../pageProperties';

// accept two points, A and B, and return the vector AB
// pass in the optional boolean to get the unit vector (true = unit vector output)
const getVector = function getVector(pointA, pointB) {
  let getUnitVector = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  // vector in the opposite directoin of A to B
  const vector = {
    x: pointB.x - pointA.x,
    y: pointB.y - pointA.y
  };
  // if getUnitVector, we only care about the direction or unit vector, so convert to unit vector
  const vectorMagnitude = getUnitVector ? Math.sqrt(vector.x * vector.x + vector.y * vector.y) : 1; // divide by 1 means it will preserve the length
  return {
    x: vector.x / vectorMagnitude,
    y: vector.y / vectorMagnitude
  };
};

// accept a point, vector and magnitude and then return a new point at
// that magnitude (vector magnitude * magnitude) and direction from the original
const extrapolateNewPoint = (origin, vector, magnitude) => {
  const newVector = {
    x: vector.x * magnitude,
    y: vector.y * magnitude
  };
  // convert back to the weird canvas system where down is positive and up is negative
  return {
    x: origin.x + newVector.x,
    y: -(origin.y + newVector.y)
  };
};

// Use the unit vector, pageNumber (to get width page width and height) and origin point
// to determine the slope and offset, and from there determine which quadrent (by reviewing
// the unit vector) and then which side (thru guessing) the vector will intersect first.
// From the intersection point, we can then determine the length from the origin point to
// the intersect point (magnitude).
const getIntersectMagnitude = (origin, unitVector, _ref) => {
  let pageHeight = _ref.pageHeight,
    pageWidth = _ref.pageWidth;
  const slope = unitVector.x === 0 ? unitVector.y : unitVector.y / unitVector.x;
  const yOffset = origin.y - slope * origin.x;
  // y = slope * x + yOffset;
  let intersectPoint = {};
  // determine which side it's going to hit
  // if uv.x === 0 -> we're hitting the TOP or the BOTTOM
  if (unitVector.x === 0) {
    intersectPoint = {
      x: origin.x,
      y: unitVector.y === -1 ? -pageHeight : 0 // must be negative to be in the right quadrent
    };
    // if uv.y === 0 -> we're hitting the RIGHT or the LEFT sides
  } else if (unitVector.y === 0) {
    intersectPoint = {
      x: unitVector.x === -1 ? 0 : pageWidth,
      y: origin.y
    };
    // if the unit vectors aren't (-)1 & 0, use functions to find the intersect
  } else {
    const x = unitVector.x > 0 ? pageWidth : 0; // true means vector is pointing right
    const y = unitVector.y < 0 ? -pageHeight : 0; // true means vector is pointing down
    const yIntercept = slope * x + yOffset; // calculate the y-intercept at the right/left extreme
    intersectPoint = yIntercept <= 0 && yIntercept >= -pageHeight ? {
      x,
      y: yIntercept
    } // intersected the left or right side first
    : {
      x: (y - yOffset) / slope,
      y
    }; // intersected the top or botton first
  }
  // Determine the new vector to the new (intersect) point
  const newVector = getVector(origin, intersectPoint);
  // Return the magnitude of the new vector
  return Math.sqrt(newVector.x * newVector.x + newVector.y * newVector.y);
};

// const y = 0;
// const x = (y - yOffset) / slope;
// intersectPoint = { x, y }; // hitting the TOP first

// append one point (or 3 in the case of ink annotations) to the end of a ink/line
// the new point(s) will ensure that the annotation appears to draw all the way to
// the edge of the page (lines = 1% off page, inks = up to 3% off page)
const ending = (points, lastPoint, lastLastPoint, lastAnnotation, selectableType) => {
  const getUnitVector = true;
  const pointA = selectableType === 'ink' ? {
    x: lastLastPoint.x,
    y: -lastLastPoint.y
  } : {
    x: lastAnnotation[0].x,
    y: -lastAnnotation[0].y
  };
  const pointB = {
    x: lastPoint.x,
    y: -lastPoint.y
  };
  // only interpolate an end point if the last two points are different
  // (two points of equal position usually implies that interpolation is not needed)
  if (pointA.x !== pointB.x || pointA.y !== pointB.y) {
    const unitVector = getVector(pointA, pointB, getUnitVector);
    const pageProps = pageProperties.getCanvasProperties(lastPoint.pageNumber);
    const mag = getIntersectMagnitude(pointB, unitVector, pageProps);
    // add a point (or 3) that is 1% (2% and 3%) longer then the calculated magnitude
    // IFF the magnitidue is less than 25% of the page width and height
    if (mag <= 0.25 * pageProps.pageHeight && mag <= 0.25 * pageProps.pageWidth) {
      points.push(_objectSpread(_objectSpread({}, lastPoint), extrapolateNewPoint(pointB, unitVector, mag * 1.01)));
      if (selectableType === 'ink') {
        // inks rely on several points to properly terminate a line,
        // whereas lines only require two points total
        points.push(_objectSpread(_objectSpread({}, lastPoint), extrapolateNewPoint(pointB, unitVector, mag * 1.02)));
        points.push(_objectSpread(_objectSpread({}, lastPoint), extrapolateNewPoint(pointB, unitVector, mag * 1.03)));
      }
    }
  }
  return points;
};

// splice in a point for all annotations after the first such that the annotation always starts
// slightly off the page when drawing from one page to another.
const beginning = (points, lastPoint, lastAnnotation, point) => {
  // if it's the 2nd point of a new line/ink and there's at least 1 previous annotation
  // lastAnnotation popped off the current annotatons, so annotaitons.length is the rest
  // need to convert points to normal cartesian sapce (need - sign for ys)
  const getUnitVector = true;
  const oldPoint = _objectSpread(_objectSpread({}, lastAnnotation[0]), {}, {
    x: lastAnnotation[0].x,
    y: -lastAnnotation[0].y
  });
  const newPoint = {
    x: point.x,
    y: -point.y
  };
  // only interpolate an end point if the last two points are different
  // (two points of equal position usually implies that interpolation is not needed)
  if (oldPoint.x !== newPoint.x || oldPoint.y !== newPoint.y) {
    const unitVector = getVector(newPoint, oldPoint, getUnitVector);
    const pageProps = pageProperties.getCanvasProperties(oldPoint.pageNumber);
    const magnitude = getIntersectMagnitude(oldPoint, unitVector, pageProps);
    // splice the new start point in between the last annotation and the newest one
    // and make the new point 1% off the page (compates to iPad behavior)
    if (magnitude <= 0.25 * pageProps.pageHeight && magnitude <= 0.25 * pageProps.pageWidth) {
      points.splice(points.length - lastAnnotation.length,
      // first point of annotation
      0, _objectSpread(_objectSpread({}, lastPoint), extrapolateNewPoint(oldPoint, unitVector, magnitude * 1.01)));
    }
  }
  return points;
};
export default {
  ending,
  beginning
};